import { FC, useState, useCallback, useMemo, useEffect } from 'react'
import useDebouncedEffect from 'use-debounced-effect'
import { CoverLetter, Resume } from '@rio/types'
import ColorPicker from 'builder/components/ColorPicker'
import { useI18n } from 'builder/hooks/useI18n'
import { DocumentTypes } from 'builder/modules/constants'
import { useTemplateCustomization } from '../../hooks/useTemplateCustomization'
import { ColorPickerPanelContainer, Title, PanelActions, PanelAction } from './styles'

interface ColorPickerPanelProps {
  handleClose: () => void
  handleColorSelect: (color: string) => void
  currentDocument: Resume | CoverLetter
}

export const ColorPickerPanel: FC<ColorPickerPanelProps> = props => {
  const { handleClose, handleColorSelect: handleSave, currentDocument } = props
  const { i18n } = useI18n()
  const [defaultColor, setDefaultColor] = useState<string>()
  const [localColor, setLocalColor] = useState<string>()

  const { supportsNewCustomization, currentTemplate, resumeSpecificTemplateSettings } =
    useTemplateCustomization(currentDocument)

  const colorSettings = useMemo(() => {
    const templateSettings = currentTemplate?.settings
    const templateColor = templateSettings?.color
    const supportsCustomColor = currentTemplate?.supportsCustomColor

    const getSelectedColor = () => {
      if (currentDocument) {
        if (currentDocument.type === DocumentTypes.resume && supportsNewCustomization) {
          return resumeSpecificTemplateSettings?.color || templateColor?.default
        }
        return currentDocument.color || templateColor?.default
      }
      return templateColor?.default || null
    }

    const selected = getSelectedColor()
    const options = templateColor?.values || []

    return { selected, options, supportsCustomColor }
  }, [
    currentTemplate?.settings,
    currentTemplate?.supportsCustomColor,
    currentDocument,
    supportsNewCustomization,
    resumeSpecificTemplateSettings?.color,
  ])

  useDebouncedEffect(
    () => {
      if (localColor && localColor !== defaultColor) {
        handleSave(localColor)
      }
    },
    350,
    [localColor],
  )

  useEffect(() => {
    if (colorSettings.selected && localColor === defaultColor) {
      setLocalColor(colorSettings.selected)
      setDefaultColor(colorSettings.selected)
    }
  }, [colorSettings.selected, defaultColor, localColor])

  const handleCancel = useCallback(() => {
    if (defaultColor) {
      handleSave(defaultColor)
      handleClose()
    }
  }, [defaultColor, handleClose, handleSave])

  const handleSaveAndClose = useCallback(() => {
    if (localColor) {
      handleSave(localColor)
      handleClose()
    }
  }, [localColor, handleSave, handleClose])

  return (
    <ColorPickerPanelContainer>
      <Title>{i18n.t('builder.resume_editor.color_select')}</Title>
      <ColorPicker hex={localColor} onChange={setLocalColor} />
      <PanelActions>
        <PanelAction onClick={handleCancel}>{i18n.t('builder.resume_editor.cancel')}</PanelAction>
        <PanelAction onClick={handleSaveAndClose}>
          {i18n.t('builder.resume_editor.save')}
        </PanelAction>
      </PanelActions>
    </ColorPickerPanelContainer>
  )
}
